import styled from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import Typography, { FontWeights } from 'builder/styles/typography'
import Media from 'builder/styles/media'
import Button from 'builder/components/Button'
import Icons from 'builder/components/Icon'
import resumePlaceholder from './assets/resume-placeholder.png'

export const Container = styled.div`
  margin: auto;
  height: fit-content;
  width: fit-content;
  display: flex;
  flex-direction: column;
  background-color: ${Colors.White};

  ${Media.Tablet`
    padding-right: 32px;
    margin-top: 30px;
  `};

  ${Media.Phone`
    margin-top: 24px;
    padding: 0 20px 32px;
  `};
`

export const Title = styled.p`
  ${Typography.L};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
`

export const Content = styled.div`
  display: flex;
  gap: 65px;
  margin-top: 12px;

  ${Media.Tablet`
    gap: 30px;
  `};

  ${Media.Phone`
    flex-direction: column;
  `};
`

export const Description = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 447px;
`

export const Subtitle = styled.p`
  ${Typography.Body};
  color: ${Colors.Neutral90};
  margin-top: 32px;

  ${Media.Phone`
    margin-top: 0;
  `};
`

export const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;

  ${Media.Phone`
    margin-top: 16px;
    gap: 16px;
  `};
`

export const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${Media.Tablet`
    align-items: flex-start;
  `};

  ${Media.Phone`
    align-items: center;
  `};
`

export const Item = styled.p`
  ${Typography.Body};
  color: ${Colors.Neutral80};

  ${Media.Phone`
    font-size: 15px;
  `};
`

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 32px;
  align-self: flex-start;
  align-items: center;
  margin-top: 60px;

  ${Media.Tablet`
    margin-top: 80px;
  `};

  ${Media.Phone`
    margin-top: 32px;
    flex-direction: column;
    align-self: center;
    width: 100%;
  `};
`

export const PremiumButton = styled(Button)`
  ${Typography.Body};
  ${FontWeights.Medium};
  color: ${Colors.White};
  background-color: ${Colors.Blue50};
  border-radius: 4px;
  width: 206px;
  padding: 12px 24px;

  &:hover {
    background-color: ${Colors.Blue60};
  }

  ${Media.Phone`
    width: 100%;
  `};
`

export const CancelSubcriptionText = styled.p`
  ${Typography.Body};
  ${FontWeights.Medium};
  color: ${Colors.Neutral50};
  cursor: pointer;

  ${Media.Phone`
    font-size: 19px;
  `};

  &:hover {
    color: ${Colors.Blue50};
  }
`

export const Icon = styled.img`
  width: 24px;
  height: 24px;

  ${Media.Phone`
    width: 20px;
    height: 20px;
  `};
`

export const Header = styled.span`
  color: ${Colors.Neutral50};
  ${Typography.Caps};
`

export const ResumeDistributionContainer = styled.div`
  padding: ${Spacing.L};
  border-radius: 12px;
  background-color: ${Colors.Neutral5};
  display: flex;
  flex-direction: column;
  cursor: pointer;
  height: fit-content;

  ${Media.Tablet`
    order: 2;
    padding: 24px;
  `};

  ${Media.Phone`
    order: unset;
  `};
`

export const ResumeDistributionHeader = styled.div`
  display: flex;
  gap: ${Spacing.M};

  ${Media.Tablet`
    flex-direction: column;
  `};

  ${Media.Phone`
    flex-direction: row;
    gap: 24px;
  `};
`

export const RecruitersAmountContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 31px;
  gap: 2px;

  ${Media.Tablet`
    order: 2;
    padding-right: 0;
  `};

  ${Media.Phone`
    order: unset;
  `};
`

export const RecruitersAmount = styled.span<{ $withMarginTop?: boolean }>`
  ${Typography.S};
  color: ${Colors.Neutral90};
  ${FontWeights.Medium};

  ${props =>
    props.$withMarginTop &&
    `
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid ${Colors.Neutral15};
  `};

  ${Media.Tablet`
    ${Typography.Subhead};
  `};

  ${Media.Phone`
    font-size: 23px;
  `};
`

export const RecruitersAmountDescriptionContainer = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;

  ${Media.Phone`
    gap: 12px;
  `};
`

export const RecruitersAmountDescription = styled.span`
  ${Typography.Caption};
  color: ${Colors.Neutral50};
`

export const Resume = styled.div<{ thumbnail?: string }>`
  width: 87px;
  height: 122px;
  background-image: url(${props => props.thumbnail || resumePlaceholder});
  box-shadow: 0px 0px 1px 0px rgba(15, 56, 113, 0.4), 0px 2px 4px 0px rgba(15, 56, 113, 0.08);
  background-size: cover;
`

export const ResumeDistributionRecruiters = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
  gap: 12px;
`

export const RecruiterContainer = styled.div`
  display: flex;
  gap: ${Spacing['3XS']};
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid ${Colors.Neutral15};

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`

export const RecruiterName = styled.span`
  ${Typography.Caption};
  color: ${Colors.Neutral90};
  ${FontWeights.Medium};
`

export const RecruiterCompany = styled.span`
  ${Typography.Caption};
  color: ${Colors.Neutral50};
`

export const TickContainer = styled.div`
  margin-left: auto;
  border-radius: 100%;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${Colors.Green50};
`

export const Tick = styled(Icons.Tick)`
  color: ${Colors.White};
`

export const ViewRecruitersButton = styled(Button)`
  margin-top: 24px;
  padding-top: 8px;
  padding-bottom: 8px;

  ${Media.Tablet`
    padding: 8px 26px;
  `};
`

export const Items = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;

  ${props =>
    props.$isMobile &&
    `
    display: none;
  `};

  ${props => Media.Phone`
    ${props.$isMobile ? 'display: flex;' : 'display: none;'}
  `};
`
